import dayjs from 'dayjs';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { ChartAnswer, ChartAnswerApi, chartAnswerSchema } from '@/entities/ChartAnswer';
import type { ChartSessionGlobalContext, ChartSessionItem } from '@/entities/ChartSession';
import { fastModeStore, notificationsStore, queryParamsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { generateId } from '@/utils/generateId';
import { schemeParse } from '@/utils/schemeParse';

import { ChartSessionsStore } from '../../shared/ChartSessionStore';
import type { ServerSelectionContext } from '../../shared/DocumentsTreeSelectionStore';
import { MetaStore } from '../../shared/MetaStore';
import { OpenStore } from '../../shared/OpenStore';
import { StreamingStatusStore } from '../../shared/StreamingStatusStore';
import { ToolCallHistoryStore, type ToolCallHistoryStep } from '../../shared/ToolCallHistoryStore';
import { FieldTextStore } from '../../shared/form/FieldTextStore';
import { SidebarDocumentsStore } from '../../sidebar/SidebarDocumentsStore';

import { AnswerRatingStore } from './AnswerRatingStore';
import { ChartModelSelectStore } from './ChartModelSelectStore';
import { ChartTipsStore } from './ChartTipsStore/ChartTipsStore';
import { ChatSseClient } from './ChatSseClient';
import { EventsOrganizationListStore } from './EventsOrganizationListStore';
import { LatestDocumentsStore } from './LatestDocumentsStore';
import { RenameSessionModalStore } from './RenameSessionModalStore';
import { ChatMessage } from './types';

type OrganizationChatAiPageStoreParams = {
  organizationId: number;
  sidebarStore: SidebarDocumentsStore;
  isModelSelectionEnabled: () => boolean;
};

type PrivateFields =
  | '_chartAnswer'
  | '_dialog'
  | '_patchMessage'
  | '_pendingSeedText'
  | '_isRestoringHistory'
  | '_analysisResultId';

export class OrganizationChatAiPageStore implements ILocalStore {
  readonly sidebarStore: SidebarDocumentsStore;
  readonly answerRatingStore: AnswerRatingStore;
  readonly eventsOrganizationListStore: EventsOrganizationListStore;
  readonly latestDocumentsStore: LatestDocumentsStore;
  readonly chartTipsStore: ChartTipsStore;
  readonly sessionsHistoryModal = new OpenStore();
  readonly renameSessionModal: RenameSessionModalStore;

  readonly fieldSelect = new ChartModelSelectStore();
  readonly fieldText = new FieldTextStore({ defaultValue: '' });

  readonly initMeta = new MetaStore();
  readonly meta = new MetaStore();
  readonly toolCallHistory = new ToolCallHistoryStore();
  private readonly _sse = new ChatSseClient();

  private readonly _chartSessions: ChartSessionsStore;
  private readonly _organizationId: number;
  private readonly _isModelSelectionEnabled: () => boolean;

  private _chartAnswer: ChartAnswer | null = null;
  private _dialog: ChatMessage[] = [];
  private _abortController: AbortController | null = null;
  private _fixedContext: ServerSelectionContext | null = null;
  private _pendingSeedText: string | null = null;
  private _isRestoringHistory = false;
  private _isSelectionLockedToAll = false;
  private _analysisResultId: number | null = null;
  // Основной документ чата по документу: документ всегда в контексте ИИ, поиск не ограничен.
  private _mainDocumentId: string | null = null;

  includeLibrary = false;
  private _pendingToolCalls = new Map<string, { displayName: string; createdAt: number }>();
  private readonly _streamingStatus = new StreamingStatusStore({
    getHasReceivedToken: () => this.hasReceivedToken,
    onStatusShown: (status) => {
      if (status.kind && status.kind !== 'call' && status.kind !== 'result') {
        this.toolCallHistory.addStep(status.kind, status.text);
      }
    },
    onQueueEmpty: () => {
      if (!this._hasFormingStatus()) {
        this.toolCallHistory.addStep('result', 'Собрал всю информацию, готовлю ответ');
      }
    },
  });

  hasReceivedToken = false;

  constructor({ organizationId, sidebarStore, isModelSelectionEnabled }: OrganizationChatAiPageStoreParams) {
    this.sidebarStore = sidebarStore;
    this._chartSessions = new ChartSessionsStore({ organizationId });
    this.renameSessionModal = new RenameSessionModalStore({ chartSessionsStore: this._chartSessions });
    this.answerRatingStore = new AnswerRatingStore({
      organizationId,
      sessionId: () => this._chartSessions.activeSessionId,
    });
    this.eventsOrganizationListStore = new EventsOrganizationListStore({ organizationId });
    this.latestDocumentsStore = new LatestDocumentsStore({ organizationId });
    this.chartTipsStore = new ChartTipsStore({ organizationId });
    this._organizationId = organizationId;
    this._isModelSelectionEnabled = isModelSelectionEnabled;

    makeObservable<OrganizationChatAiPageStore, PrivateFields>(this, {
      _chartAnswer: observable.ref,
      _dialog: observable.shallow,
      _pendingSeedText: observable,
      _isRestoringHistory: observable,
      _analysisResultId: observable,
      includeLibrary: observable,
      hasReceivedToken: observable,
      streamingStatusText: computed,
      _patchMessage: action.bound,
      cancel: action.bound,
      sendMessage: action.bound,
      openSessionsHistory: action.bound,
      closeSessionsHistory: action.bound,
      openRenameSessionModal: action.bound,
      loadMoreSessionsHistory: action.bound,
      selectSessionFromHistory: action.bound,
      resetToEmptyState: action.bound,
      setAnalysisContext: action.bound,
      restoreSessionFromUrl: action.bound,
      toggleIncludeLibrary: action.bound,
      chartAnswerMessage: computed,
      messages: computed,
      isNewSession: computed,
      isDialogEmpty: computed,
      sessionsHistory: computed,
      sessionsHistoryMeta: computed,
      sessionsHistoryLoadMoreMeta: computed,
      sessionsHistoryTotal: computed,
      hasMoreSessionsHistory: computed,
      activeSessionId: computed,
      activeSession: computed,
      loaderText: computed,
      activeToolCallHistorySteps: computed,
      activeToolCallHistoryDurationLabel: computed,
      canOpenCurrentToolCallHistory: computed,
      isModelSelectionEnabled: computed,
    });
  }

  get chartAnswerMessage() {
    return this._chartAnswer?.text;
  }

  get messages(): ChatMessage[] {
    return this._dialog;
  }

  get isNewSession(): boolean {
    return this._dialog.length === 0 && this.meta.isInitialMetaState;
  }

  get isDialogEmpty(): boolean {
    return this._dialog.length === 0;
  }

  get sessionsHistory(): ChartSessionItem[] {
    return this._chartSessions.collection.items;
  }

  get sessionsHistoryMeta(): MetaStore {
    return this._chartSessions.meta;
  }

  get chartSessionsStore(): ChartSessionsStore {
    return this._chartSessions;
  }

  get sessionsHistoryLoadMoreMeta(): MetaStore {
    return this._chartSessions.nextPageMeta;
  }

  get sessionsHistoryTotal(): number {
    return this._chartSessions.sessionsTotal;
  }

  get hasMoreSessionsHistory(): boolean {
    return this._chartSessions.hasMoreSessions;
  }

  get activeSessionId(): string | null {
    return this._chartSessions.activeSessionId;
  }

  get activeSession(): ChartSessionItem | null {
    return this._chartSessions.activeSession;
  }

  get loaderText(): string | null {
    if (this._isRestoringHistory) {
      return 'Собираю сообщения из истории';
    }

    const formingStatusText = this._getLastFormingStatusText();

    if (this.streamingStatusText && this.streamingStatusText.length > 0) {
      return this.streamingStatusText;
    }

    if (formingStatusText) {
      return formingStatusText;
    }

    return null;
  }

  get streamingStatusText(): string | null {
    return this._streamingStatus.statusText;
  }

  get activeToolCallHistorySteps(): ToolCallHistoryStep[] {
    const activeMessageId = this.toolCallHistory.activeMessageId;

    if (!activeMessageId) {
      return [];
    }

    if (this.meta.isLoading && activeMessageId === this.toolCallHistory.currentMessageId) {
      return this.toolCallHistory.steps;
    }

    const message = this._dialog.find((item) => String(item.id) === activeMessageId);

    return message?.toolCallHistory?.steps ?? [];
  }

  get activeToolCallHistoryDurationLabel(): string | null {
    const activeMessageId = this.toolCallHistory.activeMessageId;

    if (!activeMessageId) {
      return null;
    }

    if (this.meta.isLoading && activeMessageId === this.toolCallHistory.currentMessageId) {
      return this.toolCallHistory.durationLabel;
    }

    const message = this._dialog.find((item) => String(item.id) === activeMessageId);

    return message?.toolCallHistory?.durationLabel ?? null;
  }

  get canOpenCurrentToolCallHistory(): boolean {
    const currentMessageId = this.toolCallHistory.currentMessageId;

    if (!currentMessageId) {
      return false;
    }

    if (this.meta.isLoading) {
      return this.toolCallHistory.hasSteps;
    }

    const message = this._dialog.find((item) => String(item.id) === currentMessageId);

    return (message?.toolCallHistory?.steps.length ?? 0) > 0;
  }

  get isModelSelectionEnabled(): boolean {
    return this._isModelSelectionEnabled();
  }

  private _getLastFormingStatusText(): string | null {
    for (let i = this.toolCallHistory.steps.length - 1; i >= 0; i -= 1) {
      const step = this.toolCallHistory.steps[i];

      if (step?.kind !== 'status' || !('text' in step)) {
        continue;
      }

      const trimmed = step.text.trim();

      if (trimmed.toLowerCase().startsWith('формирую ')) {
        return trimmed;
      }
    }

    return null;
  }

  private _hasFormingStatus(): boolean {
    return this._getLastFormingStatusText() !== null;
  }

  async openSessionsHistory(): Promise<void> {
    this.sessionsHistoryModal.open();

    if (this.sessionsHistoryMeta.isInitialMetaState) {
      await this._chartSessions.fetchSessions({ reset: true });
    }
  }

  closeSessionsHistory(): void {
    this.sessionsHistoryModal.close();
  }

  openRenameSessionModal(sessionId: string, title: string): void {
    this.renameSessionModal.open({ sessionId, title });
  }

  async loadMoreSessionsHistory(): Promise<void> {
    if (!this.hasMoreSessionsHistory) {
      return;
    }

    await this._chartSessions.fetchMoreSessions();
  }

  async selectSessionFromHistory(sessionId: string): Promise<void> {
    this.sessionsHistoryModal.close();
    await this._restoreSessionById(sessionId);
  }

  cancel() {
    if (!this._abortController) {
      return;
    }

    this._cancelOngoingStream();
    this._streamingStatus.reset();
    this.hasReceivedToken = false;
    this.toolCallHistory.reset();

    for (let i = this._dialog.length - 1; i >= 0; i -= 1) {
      const message = this._dialog[i];

      if (message.role === 'assistant' && message.status === 'loading') {
        this._dialog[i] = { ...message, status: 'cancelled' };
        break;
      }
    }

    if (this.meta.isLoading) {
      this.meta.setLoadedSuccessMeta();
    }
  }

  private _patchMessage(id: ChatMessage['id'], patch: Partial<ChatMessage>) {
    const i = this._dialog.findIndex((m) => m.id === id);

    if (i === -1) {
      return;
    }

    this._dialog[i] = { ...this._dialog[i], ...patch };
  }

  private _cancelOngoingStream() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  private _finalizeToolCallHistory(messageId: string): void {
    const snapshot = this.toolCallHistory.snapshot();

    if (!snapshot.summaryLabel && snapshot.steps.length === 0) {
      return;
    }

    this._patchMessage(messageId, { toolCallHistory: snapshot } as Partial<ChatMessage>);
  }

  init = async () => {
    this.initMeta.setLoadedStartMeta();
    await this.sidebarStore.init();
    if (this._isModelSelectionEnabled()) {
      await this.fieldSelect.load();

      runInAction(() => {
        const opts = this.fieldSelect.options;

        if (opts.length > 0) {
          this.fieldSelect.setValue([opts[0]]);
        }
      });
    }
    this.initMeta.setLoadedSuccessMeta();
  };

  setFixedContextDocuments = (documentIds: string[]) => {
    const documentIdsList = documentIds.map(String).filter((id) => id);

    // Явно заданный контекст строгий: поиск только по нему. Чат по документу включается отдельно — setMainDocument.
    this._mainDocumentId = null;

    if (documentIdsList.length === 0) {
      this._fixedContext = null;

      return;
    }

    this._fixedContext = { documents: documentIdsList, folders: [] };
    this.sidebarStore.selectionStore.selectDocumentsOnly(documentIdsList);
  };

  setSelectionLockedToAll = (isLocked: boolean) => {
    this._isSelectionLockedToAll = isLocked;

    if (!isLocked) {
      return;
    }

    this._fixedContext = null;
    this._mainDocumentId = null;
    this.sidebarStore.selectionStore.setSelectAll(true);
    this.sidebarStore.searchDocumentStore.selectionStore.clear();
  };

  /**
   * Чат по документу (кнопка «Обсудить с ИИ»): документ всегда в контексте ИИ, а поиск идёт по всей организации.
   * Вызывать после setFixedContextDocuments/setSelectionLockedToAll — они сбрасывают основной документ.
   * Режим включает только панель чата на документе. Из сохранённой сессии он не восстанавливается:
   * в полностраничном чате выбор в дереве не всегда проходит через setFixedContextDocuments, и режим
   * незаметно отменял бы явный выбор пользователя.
   */
  setMainDocument = (documentId: string | null | undefined) => {
    this._mainDocumentId = documentId ? String(documentId) : null;
  };

  setPendingSeedText = (text: string | null | undefined) => {
    const next = text ? String(text) : null;

    this._pendingSeedText = next;

    if (next) {
      this._dialog.push({
        id: generateId(),
        role: 'user',
        text: next,
        createdAt: Date.now(),
        status: 'done',
        isMarkdown: true,
      });
    }
  };

  resetToEmptyState = () => {
    this._cancelOngoingStream();
    this._chartSessions.resetSessions();
    this._dialog = [];
    this._chartAnswer = null;
    this._pendingSeedText = null;
    this._fixedContext = null;
    this._mainDocumentId = null;
    this._isRestoringHistory = false;
    this._analysisResultId = null;
    this.includeLibrary = false;
    this.fieldText.setValue('');
    this.meta.reset();
    this._streamingStatus.reset();
    this.hasReceivedToken = false;
    this.toolCallHistory.reset();
    this._updateSessionIdInUrl(null);
  };

  async restoreSessionFromUrl(sessionId: string): Promise<void> {
    if (!sessionId) {
      return;
    }

    await this._restoreSessionById(sessionId);
  }

  private async _restoreSessionById(sessionId: string): Promise<void> {
    this._isRestoringHistory = true;
    this.meta.setLoadedStartMeta();

    await this._chartSessions.getSessionById(sessionId);
    await this._chartSessions.getSessionMessages(sessionId);

    if (this._chartSessions.getSessionByIdMeta.isError || this._chartSessions.getSessionMessagesMeta.isError) {
      runInAction(() => {
        this._isRestoringHistory = false;
        this.meta.setLoadedErrorMeta();
        this._updateSessionIdInUrl(null);
      });

      return;
    }

    runInAction(() => {
      this._applySessionContextFromActiveSession();
      const historyMessages = this._chartSessions.sessionMessages;

      this._dialog = historyMessages
        .map<ChatMessage>((message) => {
          const role = (message.role ?? 'user').toLowerCase();
          const createdAtDate = dayjs(message.createdAt);
          const createdAt = createdAtDate.isValid() ? createdAtDate.valueOf() : dayjs().valueOf();
          const isAssistant = role === 'assistant';

          return {
            id: message.id,
            role: isAssistant ? 'assistant' : 'user',
            text: message.text,
            createdAt,
            status: 'done',
            originalId: message.id,
            isMarkdown: !isAssistant,
            rating: message.rating ?? null,
          };
        })
        .sort((a, b) => a.createdAt - b.createdAt);

      this._chartAnswer = null;
      this._isRestoringHistory = false;
      this.meta.setLoadedSuccessMeta();
      this.hasReceivedToken = false;
      this.toolCallHistory.reset();
      this._updateSessionIdInUrl(sessionId);
    });
  }

  private _updateSessionIdInUrl(sessionId: string | null): void {
    queryParamsStore.setParam('session_id', sessionId);
  }

  private _buildStreamRequestPayload(message: string, activeSessionId: string) {
    const url = ENDPOINTS.chartSessions.sendMessageStream(String(this._organizationId), activeSessionId);
    const context = this._buildGlobalContext();

    const payload: { text: string; context: ChartSessionGlobalContext; is_fast: boolean; llm_model?: string } = {
      text: message,
      context,
      is_fast: fastModeStore.enabled,
    };

    if (this._isModelSelectionEnabled()) {
      payload.llm_model = this.fieldSelect.value[0]?.value ?? '';
    }

    return { url, payload } as const;
  }

  private _buildGlobalContext(): ChartSessionGlobalContext {
    const hasSearchValues = this.sidebarStore.searchDocumentStore.collection.items.length > 0;
    const baseContext =
      this._fixedContext ??
      (this._isSelectionLockedToAll
        ? { documents: [], folders: [] }
        : hasSearchValues
          ? { documents: this.sidebarStore.searchDocumentStore.selectionStore.toServerFormat(), folders: [] }
          : this.sidebarStore.selectionStore.toServerFormat());

    return {
      documents: baseContext.documents ?? [],
      folders: baseContext.folders ?? [],
      articles: this._analysisResultId ? [this._analysisResultId] : [],
      include_library: this.includeLibrary,
      main_document_id: this._mainDocumentId,
    };
  }

  private async _ensureActiveSession(): Promise<string | null> {
    if (this._chartSessions.activeSessionId) {
      return this._chartSessions.activeSessionId;
    }

    await this._chartSessions.createSession(this._buildGlobalContext());

    const newSessionId = this._chartSessions.activeSessionId;

    if (newSessionId) {
      this._updateSessionIdInUrl(newSessionId);
    }

    return newSessionId;
  }

  async sendMessage() {
    this._isRestoringHistory = false;
    const raw = this.fieldText.value ? this.fieldText.value.trim() : '';

    if (!raw) {
      return;
    }

    const activeSessionId = await this._ensureActiveSession();

    if (!activeSessionId) {
      notificationsStore.showError('Не удалось создать новую сессию, попробуйте позже');

      return;
    }

    const displayMessage = raw;
    let requestMessage = raw;
    let isMarkdown = true;

    if (this._pendingSeedText) {
      requestMessage = `Анализ:\n${this._pendingSeedText}\n\nВопрос:\n${raw}`;
      isMarkdown = true;
      this._pendingSeedText = null;
    }

    const userMsg: ChatMessage = {
      id: generateId(),
      role: 'user',
      text: displayMessage,
      createdAt: Date.now(),
      status: 'done',
      isMarkdown,
    };

    this._dialog.push(userMsg);

    const assistantId = generateId();

    this._dialog.push({
      id: assistantId,
      role: 'assistant',
      text: '',
      createdAt: Date.now(),
      status: 'loading',
    });

    this.fieldText.setValue('');

    this.meta.setLoadedStartMeta();
    this._streamingStatus.reset();
    this.hasReceivedToken = false;
    this._pendingToolCalls.clear();
    this.toolCallHistory.reset();
    this.toolCallHistory.bindToMessage(String(assistantId));
    this.toolCallHistory.start();

    try {
      this._cancelOngoingStream();
      this._abortController = new AbortController();

      const { url, payload } = this._buildStreamRequestPayload(requestMessage, activeSessionId);

      let hasStreamError = false;

      await this._sse.stream({
        url,
        body: payload,
        signal: this._abortController.signal,
        handlers: {
          onAssistantStart: () => {
            this._patchMessage(assistantId, { status: 'loading' });
          },
          onToolCall: (payload) => {
            this._pendingToolCalls.set(payload.id, {
              displayName: payload.displayName,
              createdAt: Date.now(),
            });
            const textToShow = payload.displayName.trim().length > 0 ? payload.displayName : 'Подготавливаю данные...';

            this._streamingStatus.append(textToShow, 'call');
          },
          onToolCallResult: (payload) => {
            const pending = payload.toolCallId ? this._pendingToolCalls.get(payload.toolCallId) : undefined;

            if (pending) {
              this.toolCallHistory.addToolCallStep(payload.toolCallId, pending.displayName, payload.content);
              this._pendingToolCalls.delete(payload.toolCallId);
            } else {
              this.toolCallHistory.addStep('result', payload.content);
            }
            const textToShow = pending ? pending.displayName : 'Получаю результаты инструмента...';

            this._streamingStatus.append(textToShow, 'result');
          },
          onStatus: (content) => {
            const textToShow = content && content.trim().length > 0 ? content.trim() : '';

            if (!textToShow) {
              return;
            }

            this._streamingStatus.append(textToShow, 'status');
          },
          onInformationSources: (content) => {
            const textToShow = content && content.trim().length > 0 ? content.trim() : '';

            if (!textToShow) {
              return;
            }

            const messageId = this.toolCallHistory.currentMessageId;

            this.toolCallHistory.addStep('information_sources', textToShow);

            if (messageId && this.toolCallHistory.isFinished) {
              this._finalizeToolCallHistory(messageId);
            }
          },
          onToken: (content) => {
            this._patchMessage(assistantId, { text: content, status: 'loading' });
            runInAction(() => {
              this.hasReceivedToken = true;
              if (!this._streamingStatus.hasQueuedStatuses) {
                this._streamingStatus.scheduleRotation();
              }
            });
          },
          onAnswerRestart: (message) => {
            this._patchMessage(assistantId, { text: '', status: 'loading' });
            runInAction(() => {
              this.hasReceivedToken = false;
            });
            notificationsStore.showWarning(message);
          },
          onAssistantComplete: (event) => {
            const content: string = event.text;
            const parsed = schemeParse(chartAnswerSchema, event.raw as ChartAnswerApi);

            if (parsed.success) {
              runInAction(() => {
                this._chartAnswer = parsed.data;
                this._patchMessage(assistantId, {
                  status: 'done',
                  text: parsed.data.text,
                  originalId: parsed.data.id,
                });
              });
            } else {
              this._patchMessage(assistantId, { status: 'done', text: content });
            }

            if (!this.toolCallHistory.isFinished) {
              this.toolCallHistory.finish();
            }
            this._finalizeToolCallHistory(String(assistantId));
          },
          onDone: () => {
            if (!hasStreamError) {
              this.meta.setLoadedSuccessMeta();
            }
            if (!hasStreamError) {
              const message = this._dialog.find((item) => item.id === assistantId);

              if (message?.status === 'loading') {
                this._patchMessage(assistantId, { status: 'done' });
              }
            }
            if (!this.toolCallHistory.isFinished) {
              this.toolCallHistory.finish();
              this._finalizeToolCallHistory(String(assistantId));
            }
            this._streamingStatus.reset();
            this.toolCallHistory.clearCurrentMessage();
          },
          onError: (msg) => {
            hasStreamError = true;
            notificationsStore.showError(msg);
            this._patchMessage(assistantId, { status: 'error' });
            this.meta.setLoadedErrorMeta();
            this.cancel();
          },
        },
      });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') {
        return;
      }
      notificationsStore.showError('Ошибка при формировании ответа');
      this.meta.setLoadedErrorMeta();
      this._pendingToolCalls.clear();
      this.toolCallHistory.reset();
    } finally {
      this._cancelOngoingStream();
      this._streamingStatus.reset();
      this.hasReceivedToken = false;
    }
  }

  destroy(): void {
    this.answerRatingStore.destroy();
    this.fieldSelect.destroy();
    this.fieldText.destroy();
    this.sessionsHistoryModal.destroy();
    this.renameSessionModal.destroy();
    this.eventsOrganizationListStore.destroy();
    this.latestDocumentsStore.destroy();
    this.chartTipsStore.destroy();
    this.meta.destroy();
    this._chartSessions.destroy();
    this.toolCallHistory.destroy();
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._streamingStatus.reset();
  }

  private _applySessionContextFromActiveSession() {
    const context = this._chartSessions.activeSession?.globalContext;
    const documents = context?.documents ?? [];
    const folders = context?.folders ?? [];

    this._fixedContext = documents.length > 0 || folders.length > 0 ? { documents, folders } : null;
    this._analysisResultId = context?.articles?.[0] ?? null;
  }

  toggleIncludeLibrary(): void {
    this.includeLibrary = !this.includeLibrary;
  }

  setAnalysisContext = (analysisResultId?: number | null): void => {
    const numericId = Number(analysisResultId);

    this._analysisResultId = Number.isFinite(numericId) ? numericId : null;
  };
}
